/**
 * Shared date/time utility functions for booking flows.
 * Used by CustomTimePicker, DateTimeSelector, and BookInspectionModal.
 */

import dayjs from "dayjs"

import type {
  OpeningHoursResponse,
  OptimalBookingTimesResponse,
  WeeklySchedule,
} from "@/services/firebase/functions"
import { colors } from "@/theme"

const DAY_NAMES = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
] as const

/**
 * Generate 15-minute time slots for a given time range
 */
export const generateSlotsForRange = (
  openHour: number,
  openMin: number,
  closeHour: number,
  closeMin: number,
  isToday: boolean,
  now: dayjs.Dayjs,
): string[] => {
  const slots: string[] = []
  let currentHour = openHour
  let currentMin = openMin

  const remainder = currentMin % 15
  if (remainder !== 0) {
    currentMin += 15 - remainder
    if (currentMin >= 60) {
      currentHour++
      currentMin = 0
    }
  }

  while (currentHour < closeHour || (currentHour === closeHour && currentMin + 15 <= closeMin)) {
    const timeStr = `${String(currentHour).padStart(2, "0")}:${String(currentMin).padStart(2, "0")}`

    if (!isToday) {
      slots.push(timeStr)
    } else {
      const slotTime = dayjs(`${now.format("YYYY-MM-DD")} ${timeStr}`)
      const minimumBookingTime = now.add(15, "minute")
      if (slotTime.isAfter(minimumBookingTime) || slotTime.isSame(minimumBookingTime)) {
        slots.push(timeStr)
      }
    }

    currentMin += 15
    if (currentMin >= 60) {
      currentHour++
      currentMin = 0
    }
  }

  return slots
}

/**
 * Generate time slots in 15-minute intervals based on opening hours
 */
export const generateTimeSlots = (
  selectedDate: string,
  openingHours: OpeningHoursResponse | null | undefined,
): string[] => {
  if (!openingHours?.schedule) {
    const slots: string[] = []
    for (let hour = 9; hour < 17; hour++) {
      slots.push(`${String(hour).padStart(2, "0")}:00`)
      slots.push(`${String(hour).padStart(2, "0")}:15`)
      slots.push(`${String(hour).padStart(2, "0")}:30`)
      slots.push(`${String(hour).padStart(2, "0")}:45`)
    }
    return slots
  }

  const dateObj = dayjs(selectedDate)
  const dateStr = dateObj.format("YYYY-MM-DD")
  const isToday = dateObj.isSame(dayjs(), "day")
  const now = dayjs()

  if (openingHours.specialDates && openingHours.specialDates[dateStr]) {
    const specialDate = openingHours.specialDates[dateStr]
    if (!specialDate.isOpen || !specialDate.open || !specialDate.close) return []
    const [openHour, openMin] = specialDate.open.split(":").map(Number)
    const [closeHour, closeMin] = specialDate.close.split(":").map(Number)
    return generateSlotsForRange(openHour, openMin, closeHour, closeMin, isToday, now)
  }

  const dayName = DAY_NAMES[dateObj.day()] as keyof WeeklySchedule
  const daySchedule = openingHours.schedule[dayName]

  if (!daySchedule || !daySchedule.isOpen || !daySchedule.open || !daySchedule.close) return []

  const [openHour, openMin] = daySchedule.open.split(":").map(Number)
  const [closeHour, closeMin] = daySchedule.close.split(":").map(Number)

  return generateSlotsForRange(openHour, openMin, closeHour, closeMin, isToday, now)
}

/**
 * Check if a date should be disabled in the booking calendar/strip.
 */
export const isDateDisabled = (
  date: dayjs.Dayjs,
  openingHours: OpeningHoursResponse | null | undefined,
): boolean => {
  if (!date) return false

  const dateStr = date.format("YYYY-MM-DD")
  const dayOfWeek = date.day()

  // Past dates
  if (date.isBefore(dayjs(), "day")) return true

  // Today: check if past closing time
  if (date.isSame(dayjs(), "day") && openingHours?.schedule) {
    const currentTime = dayjs().format("HH:mm")
    const dayName = DAY_NAMES[dayOfWeek] as keyof WeeklySchedule
    const daySchedule = openingHours.schedule[dayName]
    if (!daySchedule?.isOpen || (daySchedule?.close && currentTime >= daySchedule.close)) {
      return true
    }
  }

  // Holiday check
  if (openingHours?.specialDates?.[dateStr]?.isOpen === false) return true

  // Weekly schedule check
  if (openingHours?.schedule) {
    const dayName = DAY_NAMES[dayOfWeek] as keyof WeeklySchedule
    return !openingHours.schedule[dayName]?.isOpen
  }

  // Default: disable Sundays
  return dayOfWeek === 0
}

/**
 * Check if a time is in the good times list
 */
export const isGoodTime = (
  time: string,
  optimalTimes: OptimalBookingTimesResponse | null | undefined,
): boolean => {
  return optimalTimes?.goodTimes.includes(time) || false
}

/**
 * Check if a time is in the medium times list
 */
export const isMediumTime = (
  time: string,
  optimalTimes: OptimalBookingTimesResponse | null | undefined,
): boolean => {
  return optimalTimes?.mediumTimes.includes(time) || false
}

/**
 * Check if a time is in the busy times list
 */
export const isBusyTime = (
  time: string,
  optimalTimes: OptimalBookingTimesResponse | null | undefined,
): boolean => {
  return optimalTimes?.busyTimes.includes(time) || false
}

/**
 * Get the availability label for a time slot
 */
export const getAvailabilityLabel = (
  time: string,
  optimalTimes: OptimalBookingTimesResponse | null | undefined,
): { label: string; color: string; icon: string } | null => {
  if (isGoodTime(time, optimalTimes)) {
    return { label: "Schnelle Abwicklung", color: colors.primary, icon: "check-circle" }
  }
  if (isMediumTime(time, optimalTimes)) {
    return { label: "Standard Abwicklung", color: colors.text, icon: "check-circle" }
  }
  if (isBusyTime(time, optimalTimes)) {
    return { label: "Hohe Auslastung", color: colors.amber, icon: "warning" }
  }
  return null
}
